import React from "react";
import { useLang } from "../i18n";
import { Check, Users, Sparkles, MessageCircle } from "lucide-react";

const CUSTOM_WA = "https://wa.me/351931777823?text=Hello%21%20I%27d%20love%20to%20design%20a%20custom%20Porto%20Beyond%20the%20Postcard%20experience.";

export default function Pricing() {
  const { t } = useLang();
  const p = t.pricing;

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-[#F7F1E8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Editorial header with image */}
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5">
            <figure className="relative aspect-[4/5] rounded-[28px] overflow-hidden border border-[#E5D9C0] shadow-[0_40px_100px_-50px_rgba(132,118,137,0.5)]">
              <img src="/images/experiences_porto.jpg" alt="At the giant Porto letters in front of the City Hall" loading="lazy" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D3640]/20 via-transparent to-transparent" />
            </figure>
          </div>
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.28em] text-[#5E7891] mb-5">{p.eyebrow}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#2D3640]">{p.title}</h2>
            <p className="mt-5 text-base md:text-lg text-[#4A5A6B] max-w-xl">{p.sub}</p>
          </div>
        </div>

        {/* Tier cards */}
        <div className="mt-16 grid md:grid-cols-3 gap-6 items-stretch">
          {p.tiers.map((tier) => {
            const featured = tier.id === "intimate";
            return (
              <div key={tier.id} data-testid={`pricing-${tier.id}`}
                className={`relative flex flex-col p-8 md:p-9 rounded-[28px] border transition-all ${
                  featured
                    ? "bg-[#2D3640] text-[#FDFAF3] border-[#2D3640] md:-translate-y-4 shadow-[0_40px_90px_-40px_rgba(45,54,64,0.6)]"
                    : "bg-[#FDFAF3] text-[#2D3640] border-[#E5D9C0] hover:border-[#8D869B]/60"
                }`}>
                {featured && (
                  <span data-testid="pricing-featured-tag" className="absolute -top-3 left-8 inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase px-3.5 py-1.5 rounded-full bg-[#D8B07A] text-[#2D3640]">
                    <Sparkles size={12} strokeWidth={1.8} /> {p.mostLoved}
                  </span>
                )}

                <p className={`text-xs uppercase tracking-[0.22em] ${featured ? "text-[#D8B07A]" : "text-[#5E7891]"}`}>{tier.tag}</p>
                <h3 className={`font-serif-display text-3xl md:text-[2.1rem] mt-3 leading-tight ${featured ? "text-[#FDFAF3]" : "text-[#2D3640]"}`}>{tier.name}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className={`font-serif-display text-5xl ${featured ? "text-[#FDFAF3]" : "text-[#2D3640]"}`}>€{tier.price}</span>
                  <span className={`text-sm ${featured ? "text-[#C9B79A]" : "text-[#5E7891]"}`}>{p.from}</span>
                </div>
                <p className={`mt-5 text-[15px] leading-relaxed ${featured ? "text-[#FDFAF3]/80" : "text-[#4A5A6B]"}`}>{tier.blurb}</p>

                {/* Includes */}
                <p className={`mt-7 text-[11px] uppercase tracking-[0.22em] ${featured ? "text-[#D8B07A]" : "text-[#8D869B]"}`}>{p.includesLabel}</p>
                <ul className="mt-3 space-y-2.5">
                  {tier.includes.map((inc, j) => (
                    <li key={j} className={`flex gap-2.5 text-[14px] leading-snug ${featured ? "text-[#FDFAF3]/90" : "text-[#4A5A6B]"}`}>
                      <Check size={15} strokeWidth={2} className={`mt-0.5 shrink-0 ${featured ? "text-[#D8B07A]" : "text-[#5E7891]"}`} />
                      <span>{inc}</span>
                    </li>
                  ))}
                </ul>

                {/* Group + ideal */}
                <div className={`mt-7 pt-5 border-t ${featured ? "border-white/15" : "border-[#E5D9C0]"}`}>
                  <div className="flex items-center gap-2">
                    <Users size={15} strokeWidth={1.7} className={featured ? "text-[#D8B07A]" : "text-[#5E7891]"} />
                    <span className={`text-sm ${featured ? "text-[#FDFAF3]" : "text-[#2D3640]"}`}>
                      <span className={`${featured ? "text-[#C9B79A]" : "text-[#8D869B]"}`}>{p.groupLabel}: </span>
                      {tier.groupMin}–{tier.groupMax} {p.guests}
                    </span>
                  </div>
                  <p className={`mt-3 text-[13px] leading-relaxed ${featured ? "text-[#FDFAF3]/70" : "text-[#5E7891]"}`}>
                    <span className="italic">{p.idealLabel}: </span>{tier.idealFor}
                  </p>
                </div>

                <a href="#booking" onClick={() => { try { localStorage.setItem("porto_preselect_tier", tier.id); } catch {} window.dispatchEvent(new CustomEvent("porto:preselect-tier", { detail: tier.id })); }}
                  data-testid={`pricing-cta-${tier.id}`}
                  className={`btn-sweep mt-7 inline-flex items-center justify-center w-full px-6 py-3.5 rounded-full text-sm tracking-wider transition-colors ${
                    featured
                      ? "bg-[#FDFAF3] text-[#2D3640] hover:bg-[#C98C97] hover:text-[#FDFAF3]"
                      : "bg-[#2D3640] text-[#FDFAF3] hover:bg-[#5E7891]"
                  }`}>
                  <Check size={14} className="mr-2" /> {p.cta}
                </a>
              </div>
            );
          })}
        </div>

        {/* Custom Experiences */}
        <div data-testid="pricing-custom"
          className="mt-12 md:mt-16 relative overflow-hidden rounded-[30px] border border-[#E5D9C0] bg-[#F1E6D6] p-8 md:p-12">
          <div className="watercolor-blob bg-[#C98C97]" style={{ width: 260, height: 260, top: -90, right: -60 }} />
          <div className="watercolor-blob bg-[#8D869B]" style={{ width: 220, height: 220, bottom: -90, right: 120 }} />
          <div className="relative grid md:grid-cols-12 gap-8 md:gap-10 items-center">
            <div className="md:col-span-8">
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.26em] text-[#5E7891] mb-4">
                <Sparkles size={14} strokeWidth={1.6} className="text-[#C98C97]" /> {p.custom.eyebrow}
              </p>
              <h3 className="font-serif-display text-3xl md:text-4xl lg:text-[2.8rem] leading-[1.08] text-[#2D3640]">{p.custom.title}</h3>
              <p className="font-script text-2xl text-[#C98C97] mt-3">{p.custom.price}</p>
              <p className="mt-5 text-[15px] md:text-base text-[#4A5A6B] leading-relaxed max-w-2xl">{p.custom.body}</p>
              <p className="mt-3 text-[15px] md:text-base text-[#5E7891] leading-relaxed max-w-2xl">{p.custom.body2}</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <a data-testid="pricing-custom-cta" href={CUSTOM_WA} target="_blank" rel="noreferrer"
                className="btn-sweep inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#2D3640] text-[#FDFAF3] text-sm tracking-wider hover:bg-[#C98C97] transition-colors">
                <MessageCircle size={16} strokeWidth={1.7} /> {p.custom.cta}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
